import { useCallback } from 'react';
import log from '~/helpers/log';
import { reportMediaAcquisitionError } from '~/helpers/reportMediaError';

/**
 * @name useDetectMicrophone
 * @description Probe microphone access before an outgoing INVITE and report back the audio
 * constraint that actually worked.
 *
 * Its job is to answer "is there a usable microphone at all?" while the call can still be abandoned
 * cheaply - before a target is dialled, so a user with no microphone, or with permission revoked,
 * gets a toast instead of a call that rings out silently. `createRetryingMediaStreamFactory` walks
 * the same ladder from inside sip.js, so this is no longer the only line of defence for a device
 * that cannot be opened; what it adds is the abort and the message.
 *
 * The constraint it returns is what the INVITE is built with, so it also decides whether the
 * `X-User-Device-Info` header can name the device redux holds.
 *
 * Callers are expected to hand over a constraint and ladder that already passed
 * `resolveAudioConstraints`, so a device that has simply been unplugged never reaches getUserMedia
 * at all - the remaining failures are the ones only an acquisition can reveal: no microphone,
 * permission revoked, or a device that is attached but not openable.
 *
 * @returns {(
 *   preferred?: true | {deviceId: {exact: string}},
 *   fallbacks?: Array<true | {deviceId: {exact: string}}>
 * ) => Promise<true | {deviceId: {exact: string}} | null>} the constraint that opened the
 * microphone, or null when none did. `null` is the only falsy result - callers test truthiness.
 */
export default function useDetectMicrophone() {
  const detectMicrophone = useCallback(
    async (preferred = true, fallbacks = []) => {
      // The ladder comes from `resolveAudioConstraints`, which knows what the platform enumerates.
      // An unconstrained request has nothing to fall back from, so the happy path stays at exactly
      // one acquisition.
      const attempts = [preferred, ...fallbacks];

      for (let i = 0; i < attempts.length; i += 1) {
        const audio = attempts[i];

        try {
          // eslint-disable-next-line no-await-in-loop
          const stream = await navigator.mediaDevices.getUserMedia({
            audio,
            video: false,
          });
          const { active } = stream;

          // Close it straight away: this only answers "can we open it?". sip.js opens its own.
          stream.getTracks().forEach(track => track.stop());

          if (active) {
            log.info('Accessed the Microphone');
            return audio;
          }
        } catch (err) {
          reportMediaAcquisitionError(err, {
            direction: 'outgoing',
            probe: true,
            // An attempt still to come means the call is not lost yet, only the preferred device.
            recovered: i < attempts.length - 1,
            deviceId: audio === true ? null : audio?.deviceId?.exact,
            constraint: err?.constraint ?? null,
            attempt: i,
          });
        }
      }

      log.error('Failed to get an available microphone.');

      return null;
    },
    []
  );

  return detectMicrophone;
}
